import type { EventType, RealtimeEvent } from "@luckymenu/core";
import type { RealtimeClient } from "./client";

/**
 * Which cached queries an event makes stale. Keys are tRPC path prefixes as
 * TanStack Query keys: `[["staff", "ordering"]]` matches every
 * `staff.ordering.*` query made with `@trpc/tanstack-react-query`, whatever
 * its input (TanStack matches key prefixes).
 */
export type QueryKey = readonly unknown[];
export type InvalidationMap = Readonly<Record<EventType, readonly QueryKey[]>>;

const staffOrdering: QueryKey = [["staff", "ordering"]];
const staffBilling: QueryKey = [["staff", "billing"]];

/** For both staff apps. */
export const STAFF_INVALIDATIONS: InvalidationMap = {
	"batch.created": [staffOrdering],
	"batch.accepted": [staffOrdering],
	"batch.rejected": [staffOrdering],
	"order.updated": [staffOrdering],
	"order.closed": [staffOrdering, staffBilling],
	"kot.updated": [staffOrdering],
	"service.requested": [staffOrdering],
	"service.updated": [staffOrdering],
	"bill.finalized": [staffOrdering, staffBilling],
	"bill.voided": [staffOrdering, staffBilling],
	"payment.recorded": [staffOrdering, staffBilling],
	"payment.claimed": [staffOrdering, staffBilling],
	"menu.changed": [[["staff", "catalog"]]],
	"printjob.created": [[["staff", "printing"]]],
	"printjob.updated": [[["staff", "printing"]]],
	"table.updated": [staffOrdering, [["staff", "tables"]]],
	"restaurant.updated": [[["staff", "tenancy"]], staffOrdering, staffBilling],
	/** Sites are edited in admin; the staff apps show nothing from them. */
	"site.changed": [],
	/** Owners are told by push (the worker); open screens show the new UPI ID. */
	"upi.changed": [staffBilling],
	"ai.job.updated": [],
};

const publicOrdering: QueryKey = [["public", "ordering"]];
const publicBilling: QueryKey = [["public", "billing"]];

/** For the storefront. Diners hear only table and public events. */
export const GUEST_INVALIDATIONS: InvalidationMap = {
	"batch.created": [publicOrdering],
	"batch.accepted": [publicOrdering],
	"batch.rejected": [publicOrdering],
	"order.updated": [publicOrdering],
	"order.closed": [publicOrdering, publicBilling],
	"kot.updated": [],
	"service.requested": [publicOrdering],
	"service.updated": [publicOrdering],
	"bill.finalized": [publicOrdering, publicBilling],
	"bill.voided": [publicOrdering, publicBilling],
	"payment.recorded": [publicOrdering, publicBilling],
	"payment.claimed": [],
	"menu.changed": [[["public", "catalog"]]],
	"printjob.created": [],
	"printjob.updated": [],
	"table.updated": [],
	"restaurant.updated": [[["public", "tenancy"]], publicOrdering],
	"site.changed": [],
	"upi.changed": [],
	"ai.job.updated": [],
};

/** The queries of live screens: refetched after a reconnect and polled while offline. */
export const STAFF_LIVE_KEYS: readonly QueryKey[] = [staffOrdering];
export const GUEST_LIVE_KEYS: readonly QueryKey[] = [publicOrdering];

/** The part of TanStack Query's `QueryClient` this needs. */
export interface InvalidatingQueryClient {
	invalidateQueries(filters: { queryKey: QueryKey }): Promise<unknown>;
}

export type BindOptions = {
	invalidations: InvalidationMap;
	liveKeys: readonly QueryKey[];
	/** Poll live queries this often while offline. Defaults to 15 seconds. */
	pollMs?: number;
};

/**
 * Keeps a query cache fresh from the socket: each event invalidates its keys
 * (active queries refetch), a reconnect invalidates the live keys, and while
 * the client is offline the live keys are invalidated every 15 seconds.
 * Returns a function that undoes it.
 */
export function bindQueryClient(
	client: RealtimeClient,
	queryClient: InvalidatingQueryClient,
	options: BindOptions,
): () => void {
	const invalidate = (keys: readonly QueryKey[]) => {
		for (const queryKey of keys) void queryClient.invalidateQueries({ queryKey });
	};
	const offEvent = client.onEvent((event: RealtimeEvent) =>
		invalidate(options.invalidations[event.type] ?? []),
	);
	const offReconnect = client.onReconnect(() => invalidate(options.liveKeys));

	let poll: ReturnType<typeof setInterval> | undefined;
	const sync = () => {
		const offline = client.getStatus().offline;
		if (offline && !poll) {
			invalidate(options.liveKeys);
			poll = setInterval(() => invalidate(options.liveKeys), options.pollMs ?? 15_000);
		} else if (!offline && poll) {
			clearInterval(poll);
			poll = undefined;
		}
	};
	const offStatus = client.subscribe(sync);
	sync();

	return () => {
		offEvent();
		offReconnect();
		offStatus();
		if (poll) clearInterval(poll);
	};
}
